import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Send, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useServiceMap } from "@/lib/data";
import { ServiceIcon } from "@/lib/icons";
import { Avatar, Rating } from "@/components/Bits";
import BackButton from "@/components/BackButton";

export default function ProviderPublic() {
  const { id } = useParams();
  const services = useServiceMap();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["provider", id], queryFn: () => api.get(`/providers/${id}`).then((r) => r.data) });
  if (isLoading) return <div className="flex justify-center py-24"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>;
  if (error) return <p className="py-24 text-center text-xl">{errMsg(error)}</p>;
  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6 sm:px-6">
      <BackButton />
      <div className="po-card">
        <div className="flex gap-4">
          <Avatar photo={p.photo} name={p.name} size="h-24 w-24" />
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900" data-testid="provider-public-name">{p.name}</h1>
            <Rating avg={p.rating_avg} count={p.rating_count} />
            <p className="mt-1 flex items-center gap-1 text-lg text-slate-700"><MapPin className="h-5 w-5" /> {p.base_neighborhood}, {p.base_city}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {p.services.map((s) => (
            <span key={s} className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-base font-bold text-orange-800">
              <ServiceIcon icon={services[s]?.icon} className="h-5 w-5" /> {services[s]?.name || s}
            </span>
          ))}
        </div>
        {p.description && <p className="mt-4 text-lg text-slate-800">{p.description}</p>}
        <p className="mt-3 text-base text-slate-600">Atende em: {p.areas.map((a) => `${a.city}${a.neighborhoods?.length ? ` (${a.neighborhoods.join(", ")})` : " (cidade toda)"}`).join("; ")}</p>
        {p.available ? (
          <Link to={`/pedido/novo?servico=${p.primary_service}&prestador=${p.id}`} className="po-btn-primary mt-5 w-full" data-testid="provider-public-request"><Send className="h-6 w-6" /> Pedir orçamento</Link>
        ) : <p className="mt-5 rounded-2xl bg-slate-100 p-4 text-lg text-slate-700">Este prestador está indisponível no momento.</p>}
      </div>
      <div className="po-card" data-testid="provider-reviews">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold"><Star className="h-6 w-6 text-yellow-400" /> Avaliações</h2>
        {p.reviews.length === 0 && <p className="mt-2 text-lg text-slate-600">Ainda sem avaliações.</p>}
        <ul className="mt-3 space-y-3">
          {p.reviews.map((r) => (
            <li key={r.id} className="rounded-2xl bg-slate-50 p-4">
              <p className="text-lg font-bold text-yellow-500">{"★".repeat(r.stars)}<span className="text-slate-300">{"★".repeat(5 - r.stars)}</span></p>
              {r.comment && <p className="text-lg text-slate-800">{r.comment}</p>}
              <p className="text-sm text-slate-500">{r.client_name} · {new Date(r.created_at).toLocaleDateString("pt-BR")}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
